import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Plus, Search, Phone, Mail, MailOpen, Trash2, Pencil, Building2, UserPlus, ReceiptText, MessageSquareText, PhoneCall, ChevronDown, Paperclip, MessageCircle, Users, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";
import ClientPortalPanel from "@/components/ClientPortalPanel";
import CallCapture from "@/components/CallCapture";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const EMPTY_CLIENT = { name: "", email: "", phone: "", address: "", notes: "", bank_account_id: "", default_price: "" };
const EMPTY_CONTACT = { name: "", role: "", email: "", phone: "" };
const DEFAULT_ROLES = ["Main contact", "Secondary contact", "Safety officer", "Accountant", "Secretary", "Chairman", "Owner", "Event organiser"];
const EMPTY_ACTIVITY = { type: "call", contact_name: "", notes: "", follow_up_title: "", follow_up_date: "", follow_up_time: "" };
const ACTIVITY_LABEL = { call: "Call", meeting: "Meeting", email: "Email", note: "Note" };

export default function ClientContacts() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [search, setSearch] = useState("");
  const [clientDialog, setClientDialog] = useState(false);
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);
  const [editingClient, setEditingClient] = useState(null);
  const [contactDialog, setContactDialog] = useState(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [activityDialog, setActivityDialog] = useState(null);
  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY);
  const [activityFile, setActivityFile] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState("");
  const [activities, setActivities] = useState({});
  const [view, setView] = useState("companies");
  const [searchParams] = useSearchParams();
  const focusClient = searchParams.get("client") || "";
  const [roleOptions, setRoleOptions] = useState(DEFAULT_ROLES);
  const [callOpen, setCallOpen] = useState(false);

  const toggleActivity = async (c) => {
    if (expandedActivity === c.id) {
      setExpandedActivity("");
      return;
    }
    setExpandedActivity(c.id);
    try {
      const { data } = await api.get(`/crm/clients/${c.id}/interactions`);
      setActivities((m) => ({ ...m, [c.id]: data }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveActivity = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/crm/clients/${activityDialog.id}/interactions`, activityForm);
      if (activityFile && data.id) {
        const fd = new FormData();
        fd.append("file", activityFile);
        await api.post(`/crm/interactions/${data.id}/attachment`, fd);
      }
      toast.success(data.message);
      const cid = activityDialog.id;
      setActivityDialog(null);
      setActivityForm(EMPTY_ACTIVITY);
      setActivityFile(null);
      if (expandedActivity === cid) {
        const { data: rows } = await api.get(`/crm/clients/${cid}/interactions`);
        setActivities((m) => ({ ...m, [cid]: rows }));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: banks }] = await Promise.all([api.get("/clients"), api.get("/bank-accounts")]);
      setClients(data);
      setBankAccounts(banks.accounts || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveClient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingClient) {
        await api.patch(`/clients/${editingClient.id}`, { ...clientForm, default_price: clientForm.default_price === "" ? null : Number(clientForm.default_price) });        toast.success("Client updated");
      } else {
        await api.post("/clients", { ...clientForm, default_price: clientForm.default_price === "" ? null : Number(clientForm.default_price) });
        toast.success("Client added");
      }
      setClientDialog(false);
      setEditingClient(null);
      setClientForm(EMPTY_CLIENT);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete ${c.name} and all its contacts?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (contactForm.contact_id) {
        await api.patch(`/clients/${contactDialog.id}/contacts/${contactForm.contact_id}`, contactForm);
        toast.success("Contact updated");
      } else {
        await api.post(`/clients/${contactDialog.id}/contacts`, contactForm);
        toast.success("Contact added");
      }
      setContactDialog(null);
      setContactForm(EMPTY_CONTACT);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (clientId, contact) => {
    if (!window.confirm(`Remove ${contact.name} from this client?`)) return;
    try {
      await api.delete(`/clients/${clientId}/contacts/${contact.contact_id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteActivity = async (cid, a) => {
    if (!window.confirm("Delete this log entry? Any follow-up job it created (if not done yet) is removed too.")) return;
    try {
      await api.delete(`/crm/clients/${cid}/interactions/${a.id}`);
      toast.success("Log entry deleted");
      const { data: rows } = await api.get(`/crm/clients/${cid}/interactions`);
      setActivities((m) => ({ ...m, [cid]: rows }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = (clients || []).filter((c) =>
    (!focusClient || c.id === focusClient) &&
    (!search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.contacts.some((ct) => ct.name.toLowerCase().includes(search.toLowerCase()))));

  const digits = (s) => (s || "").replace(/\D/g, "");

  const loadRoles = useCallback(() => {
    api.get("/contact-roles").then((r) => setRoleOptions(r.data.roles)).catch((e) => console.debug("Contact roles load failed:", e?.message || e));
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const makeMain = async (clientId, contact) => {
    try {
      await api.post(`/clients/${clientId}/contacts/${contact.contact_id}/make-main`);
      toast.success(`${contact.name} is now the main contact`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const allPeople = (clients || []).flatMap((c) => [
    ...((c.phone || c.email) ? [{ key: `main-${c.id}`, name: c.name, role: "Main line", phone: c.phone || "", email: c.email || "", clientName: "", client: c, contact: null }] : []),
    ...(c.contacts || []).map((ct) => ({ key: ct.contact_id, name: ct.name, role: ct.role || "", phone: ct.phone || "", email: ct.email || "", clientName: c.name, client: c, contact: ct })),
  ]);
  const q = search.trim().toLowerCase();
  const qd = digits(search);
  const matchingPeople = q ? allPeople.filter((p) =>
    p.name.toLowerCase().includes(q) || (p.role || "").toLowerCase().includes(q) ||
    (p.clientName || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q) ||
    (qd.length >= 3 && digits(p.phone).includes(qd))) : [];

  const waHref = (phone) => {
    let p = digits(phone);
    if (p.startsWith("0")) p = "44" + p.slice(1);
    return `https://wa.me/${p}`;
  };

  const renderPersonRow = (p, prefix) => (
    <div key={p.key} className="flex items-center gap-2 flex-wrap rounded-xl border border-slate-200 bg-white px-3 py-2.5" data-testid={`${prefix}-${p.key}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0F2547] truncate">{p.name}{p.role ? <span className="font-normal text-slate-500"> · {p.role}</span> : ""}</p>
        {p.clientName && <p className="text-[11px] text-slate-400 truncate">{p.clientName}</p>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {p.phone && (<>
          <a href={`tel:${p.phone}`} data-testid={`${prefix}-call-${p.key}`} title={`Call ${p.phone}`}
            className="h-9 w-9 grid place-items-center rounded-lg bg-[#007F3B] hover:bg-[#006631] text-white transition-colors"><Phone className="h-3.5 w-3.5" /></a>
          <a href={waHref(p.phone)} target="_blank" rel="noreferrer" data-testid={`${prefix}-wa-${p.key}`} title="Open WhatsApp chat"
            className="h-9 w-9 grid place-items-center rounded-lg bg-[#25D366] hover:bg-[#1fb857] text-white transition-colors"><MessageCircle className="h-3.5 w-3.5" /></a>
        </>)}
        {p.email && (<>
          <button onClick={() => navigate(`/admin/emails?compose=1&to=${encodeURIComponent(p.email)}`)} data-testid={`${prefix}-email-${p.key}`} title={`Email ${p.email} from your WEMS mailbox`}
            className="h-9 w-9 grid place-items-center rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white transition-colors"><Mail className="h-3.5 w-3.5" /></button>
          <a href={`mailto:${p.email}`} data-testid={`${prefix}-mailto-${p.key}`} title={`Open ${p.email} in your device's mail app`}
            className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors"><MailOpen className="h-3.5 w-3.5" /></a>
        </>)}
        {p.contact && (
          <button onClick={() => { setContactDialog(p.client); setContactForm(p.contact); }} data-testid={`${prefix}-edit-${p.key}`} title="Edit this person"
            className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
        )}
      </div>
    </div>
  );

  const peopleByLetter = (() => {
    const groups = {};
    [...allPeople].sort((a, b) => a.name.localeCompare(b.name)).forEach((p) => {
      const L = (p.name[0] || "#").toUpperCase();
      (groups[L] = groups[L] || []).push(p);
    });
    return Object.keys(groups).sort().map((L) => ({ letter: L, people: groups[L] }));
  })();

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="client-contacts-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/admin")} data-testid="contacts-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]" data-testid="contacts-title">Client contacts</h1>
          <button onClick={() => setCallOpen(true)} data-testid="on-the-phone-btn"
            className="ml-auto h-11 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-sm font-bold inline-flex items-center gap-1.5">
            <PhoneCall className="h-4 w-4" /> On the phone
          </button>
          <button onClick={() => { setEditingClient(null); setClientForm(EMPTY_CLIENT); setClientDialog(true); }}
            data-testid="add-client-btn"
            className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold inline-flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Add company / client
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6">
        <p className="text-xs text-slate-500 mb-4">Add each company (or person) you invoice once, then keep their people, emails and bank details here. When you raise an invoice you'll pick exactly who in the organisation receives it.</p>
        <div className="relative mb-5">
          <Search className="h-4 w-4 absolute left-3 top-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search any person, company, phone, email or role…"
            data-testid="contacts-search"
            className="w-full h-12 rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
        </div>

        {clients !== null && !search && (
          <div className="flex gap-2 mb-4" data-testid="contacts-view-tabs">
            <button onClick={() => setView("companies")} data-testid="contacts-view-companies"
              className={`h-10 px-4 rounded-lg text-xs font-bold transition-colors ${view === "companies" ? "bg-[#005EB8] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-[#005EB8]"}`}>
              <Building2 className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />By company
            </button>
            <button onClick={() => setView("people")} data-testid="contacts-view-people"
              className={`h-10 px-4 rounded-lg text-xs font-bold transition-colors ${view === "people" ? "bg-[#005EB8] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-[#005EB8]"}`}>
              <Users className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />People A–Z
            </button>
          </div>
        )}
        {focusClient && (
          <button onClick={() => navigate("/admin/contacts")} data-testid="contacts-clear-focus"
            className="mb-3 text-xs font-bold text-[#005EB8] hover:underline">
            ← Back to all clients
          </button>
        )}
        <CallCapture open={callOpen} onOpenChange={setCallOpen} />
        {clients === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : search ? (
          <div className="space-y-2" data-testid="contacts-search-results">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">
              {matchingPeople.length} {matchingPeople.length === 1 ? "person" : "people"} matching &ldquo;{search}&rdquo;
            </p>
            {matchingPeople.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="contacts-search-empty">
                <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No one matches — check the spelling or try a phone number.</p>
              </div>
            ) : matchingPeople.map((p) => renderPersonRow(p, "search-person"))}
          </div>
        ) : view === "people" ? (
          <div className="space-y-4" data-testid="contacts-people-dir">
            {peopleByLetter.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="contacts-people-empty">
                <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No people saved yet — add contacts to your clients first.</p>
              </div>
            ) : peopleByLetter.map((g) => (
              <div key={g.letter}>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5" data-testid={`people-letter-${g.letter}`}>{g.letter}</p>
                <div className="space-y-2">{g.people.map((p) => renderPersonRow(p, "person"))}</div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="contacts-empty">
            <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No clients yet — add your first one above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5" data-testid={`client-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="min-w-0">
                    <p className="font-heading text-lg font-bold text-[#0F2547] flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#005EB8]" /> {c.name}
                    </p>
                    {c.notes && <p className="text-xs text-slate-500 mt-0.5">{c.notes}</p>}
                    {(c.phone || c.email) && (
                      <div className="flex gap-2 mt-1.5 flex-wrap">
                        {c.phone && (
                          <a href={`tel:${c.phone}`} data-testid={`client-call-${c.id}`}
                            className="h-9 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </a>
                        )}
                        {c.email && (<>
                          <button onClick={() => navigate(`/admin/emails?compose=1&to=${encodeURIComponent(c.email)}`)} data-testid={`client-email-${c.id}`}
                            title="Email from your WEMS mailbox"
                            className="h-9 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                            <Mail className="h-3 w-3" /> {c.email}
                          </button>
                          <a href={`mailto:${c.email}`} data-testid={`client-mailto-${c.id}`} title="Open in your device's mail app"
                            className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors">
                            <MailOpen className="h-3.5 w-3.5" />
                          </a>
                        </>)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(c.invoice_count > 0) && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 bg-sky-50 text-[#005EB8]" data-testid={`client-invoices-${c.id}`}>
                        <ReceiptText className="h-3 w-3" /> {c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"}
                        {c.outstanding_total > 0 && <span className="text-[#ED8B00]"> · £{c.outstanding_total.toFixed(2)} outstanding{c.outstanding_due ? ` · due ${new Date(`${c.outstanding_due}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}</span>}
                      </span>
                    )}
                    <button onClick={() => { setEditingClient(c); setClientForm({ name: c.name, email: c.email || "", phone: c.phone || "", address: c.address || "", notes: c.notes, bank_account_id: c.bank_account_id || "", default_price: c.default_price ?? "" }); setClientDialog(true); }}
                      data-testid={`client-edit-${c.id}`}
                      className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => deleteClient(c)} data-testid={`client-delete-${c.id}`}
                      className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">People at {c.name}</p>
                  {c.contacts.map((ct) => (
                    <div key={ct.contact_id} className="flex items-center justify-between gap-2 flex-wrap rounded-xl border border-slate-200 bg-[#F4F7FA] px-3 py-2.5" data-testid={`contact-row-${ct.contact_id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-[#0F2547]">{ct.name}{ct.role ? <span className="font-normal text-slate-500"> · {ct.role}</span> : ""}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {ct.phone && (
                          <a href={`tel:${ct.phone}`} data-testid={`contact-call-${ct.contact_id}`}
                            className="h-10 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5" /> {ct.phone}
                          </a>
                        )}
                        {ct.email && (<>
                          <button onClick={() => navigate(`/admin/emails?compose=1&to=${encodeURIComponent(ct.email)}`)} data-testid={`contact-email-${ct.contact_id}`}
                            title="Email from your WEMS mailbox"
                            className="h-10 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5" /> {ct.email}
                          </button>
                          <a href={`mailto:${ct.email}`} data-testid={`contact-mailto-${ct.contact_id}`} title="Open in your device's mail app"
                            className="h-10 w-10 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors">
                            <MailOpen className="h-3.5 w-3.5" />
                          </a>
                        </>)}
                        {ct.role === "Main contact" ? (
                          <span className="h-9 w-9 grid place-items-center rounded-lg bg-amber-100 text-[#ED8B00]" title="Main contact" data-testid={`contact-main-${ct.contact_id}`}>
                            <Star className="h-3.5 w-3.5" fill="currentColor" />
                          </span>
                        ) : (
                          <button onClick={() => makeMain(c.id, ct)} data-testid={`contact-make-main-${ct.contact_id}`} title="Make main contact"
                            className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#ED8B00] hover:border-[#ED8B00] transition-colors bg-white">
                            <Star className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => { setContactDialog(c); setContactForm(ct); }}
                          data-testid={`contact-edit-${ct.contact_id}`}
                          className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:text-[#005EB8] transition-colors bg-white">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => deleteContact(c.id, ct)} data-testid={`contact-delete-${ct.contact_id}`}
                          className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors bg-white">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => { setContactDialog(c); setContactForm(EMPTY_CONTACT); }}
                    data-testid={`contact-add-${c.id}`}
                    className="w-full h-11 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center justify-center gap-1.5">
                    <UserPlus className="h-4 w-4" /> Add person (name, job title, email, phone)
                  </button>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => toggleActivity(c)} data-testid={`activity-toggle-${c.id}`}
                      className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5">
                      <MessageSquareText className="h-3.5 w-3.5" /> Activity log
                      <ChevronDown className={`h-3 w-3 transition-transform ${expandedActivity === c.id ? "rotate-180" : ""}`} />
                    </button>
                    <button onClick={() => { setActivityDialog(c); setActivityForm(EMPTY_ACTIVITY); setActivityFile(null); }} data-testid={`activity-log-btn-${c.id}`}
                      className="h-9 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                      <PhoneCall className="h-3.5 w-3.5" /> Log call or conversation
                    </button>
                    <ClientPortalPanel client={c} />
                  </div>
                  {expandedActivity === c.id && (
                    <div className="mt-2 space-y-2" data-testid={`activity-list-${c.id}`}>
                      {(activities[c.id] || []).length === 0 ? (
                        <p className="text-xs text-slate-400 px-1 py-1">No calls or meetings logged yet — use the green button after your next one.</p>
                      ) : (activities[c.id] || []).map((a) => (
                        <div key={a.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 flex items-start gap-2" data-testid={`activity-row-${a.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-bold text-slate-500 flex items-center gap-1.5 flex-wrap">
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${{ call: "bg-emerald-50 text-[#007F3B]", meeting: "bg-violet-50 text-violet-700", email: "bg-sky-50 text-[#005EB8]", note: "bg-slate-100 text-slate-600" }[a.type] || "bg-slate-100 text-slate-600"}`}>
                                {ACTIVITY_LABEL[a.type] || a.type}
                              </span>
                              {a.contact_name && <span>with {a.contact_name}</span>} ·{" "}
                              {new Date(a.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {a.created_by}
                              {a.follow_up_task_id && <span className="text-[#ED8B00]"> · follow-up in To-Do</span>}
                            </p>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5">{a.notes}</p>
                            {a.attachment && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/interactions/${a.id}/attachment`}
                                data-testid={`activity-attachment-${a.id}`}
                                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-bold text-[#005EB8] hover:underline">
                                <Paperclip className="h-3.5 w-3.5" /> {a.attachment.filename}
                              </a>
                            )}
                          </div>
                          <button onClick={() => deleteActivity(c.id, a)} data-testid={`activity-delete-${a.id}`} title="Delete this log entry"
                            className="h-8 w-8 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors shrink-0">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={clientDialog} onOpenChange={setClientDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">{editingClient ? "Edit company / client" : "Add company or client"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveClient} className="space-y-3 mt-1">
            <input required placeholder="Company name — or just a person's name if they're not a company" value={clientForm.name}
              onChange={(e) => setClientForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="client-form-name" className={inputCls} />
            <input type="email" placeholder="Main email (invoices go here if no person picked)" value={clientForm.email}
              onChange={(e) => setClientForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="client-form-email" className={inputCls} />
            <input type="tel" placeholder="Main phone (tap-to-call)" value={clientForm.phone}
              onChange={(e) => setClientForm((f) => ({ ...f, phone: e.target.value }))}
              data-testid="client-form-phone" className={inputCls} />
            <input placeholder="Address" value={clientForm.address}
              onChange={(e) => setClientForm((f) => ({ ...f, address: e.target.value }))}
              data-testid="client-form-address" className={inputCls} />
            <textarea rows={2} placeholder="Notes (what they book us for, anything useful)" value={clientForm.notes}
              onChange={(e) => setClientForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="client-form-notes"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input type="number" min="0" step="0.01" placeholder="Default price per event £ (e.g. Dawn always £250)" value={clientForm.default_price}
              onChange={(e) => setClientForm((f) => ({ ...f, default_price: e.target.value }))}
              data-testid="client-form-default-price" className={inputCls} />
            <p className="text-[11px] text-slate-400 -mt-1.5">Set once — every new invoice for this client pre-fills this price. You can still change it on any invoice.</p>
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Which account should this client pay into?</p>
              <select value={clientForm.bank_account_id}
                onChange={(e) => setClientForm((f) => ({ ...f, bank_account_id: e.target.value }))}
                data-testid="client-form-bank-account" className={`${inputCls} bg-white`}>
                {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.label} — {a.bank_name}</option>)}
              </select>
              <p className="text-[11px] text-slate-400">Manage your accounts from Invoices → Bank accounts</p>
            </div>
            <button type="submit" disabled={saving} data-testid="client-form-save"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold">
              {saving ? "Saving…" : editingClient ? "Save changes" : "Add client"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!contactDialog} onOpenChange={() => setContactDialog(null)}>
        <DialogContent className="max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">
              {contactForm.contact_id ? "Edit contact" : `Add contact — ${contactDialog?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { const r = contactForm.role.trim(); if (r && !roleOptions.includes(r)) setRoleOptions((o) => [...o, r]); saveContact(e); }} className="space-y-3 mt-1">
            <input required placeholder="Name" value={contactForm.name}
              onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="contact-form-name" className={inputCls} />
            <input placeholder="Role (e.g. Main contact, Accounts)" value={contactForm.role}
              onChange={(e) => setContactForm((f) => ({ ...f, role: e.target.value }))}
              list="contact-role-options"
              data-testid="contact-form-role" className={inputCls} />
            <datalist id="contact-role-options">
              {roleOptions.map((r) => <option key={r} value={r} />)}
            </datalist>
            <p className="text-[11px] text-slate-400 -mt-1">Pick one or type your own (e.g. Safety officer) — new roles are saved for next time.</p>
            <input type="email" placeholder="Email" value={contactForm.email}
              onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="contact-form-email" className={inputCls} />
            <input type="tel" placeholder="Phone (tap-to-call on mobile)" value={contactForm.phone}
              onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
              data-testid="contact-form-phone" className={inputCls} />
            <button type="submit" disabled={saving} data-testid="contact-form-save"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold">
              {saving ? "Saving…" : contactForm.contact_id ? "Save changes" : "Add contact"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activityDialog} onOpenChange={() => setActivityDialog(null)}>
        <DialogContent className="max-w-md" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Log call or conversation — {activityDialog?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveActivity} className="space-y-3 mt-1">
            <div className="grid grid-cols-2 gap-2">
              <select value={activityForm.type} data-testid="activity-form-type"
                onChange={(e) => setActivityForm((f) => ({ ...f, type: e.target.value }))}
                className={`${inputCls} bg-white`}>
                <option value="call">Phone call</option>
                <option value="meeting">Meeting</option>
                <option value="email">Email</option>
                <option value="note">Note</option>
              </select>
              <select value={activityForm.contact_name} data-testid="activity-form-person"
                onChange={(e) => setActivityForm((f) => ({ ...f, contact_name: e.target.value }))}
                className={`${inputCls} bg-white`}>
                <option value="">Anyone / general</option>
                {(activityDialog?.contacts || []).map((ct) => (
                  <option key={ct.contact_id} value={ct.name}>{ct.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-slate-600">What was discussed</span>
                <VoiceButton testid="activity-voice"
                  onTranscript={(t) => setActivityForm((f) => ({ ...f, notes: (f.notes ? f.notes + " " : "") + t }))} />
              </div>
              <textarea required rows={4} value={activityForm.notes} data-testid="activity-form-notes"
                onChange={(e) => setActivityForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Notes — type or tap the mic and talk…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-600 block mb-1">Attach a file (optional — photo, letter, anything)</span>
              <label className="flex items-center gap-2 h-12 px-3 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 cursor-pointer hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                <Paperclip className="h-4 w-4 shrink-0" />
                <span className="truncate">{activityFile ? activityFile.name : "Choose a file…"}</span>
                <input type="file" className="hidden" data-testid="activity-form-file"
                  onChange={(e) => setActivityFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Follow-up job (optional — goes straight into To-Do)</p>
              <input placeholder="e.g. Chase invoice, send quote, ring back Tuesday" value={activityForm.follow_up_title}
                onChange={(e) => setActivityForm((f) => ({ ...f, follow_up_title: e.target.value }))}
                data-testid="activity-form-followup" className={inputCls} />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={activityForm.follow_up_date} title="Do it by this date (optional)"
                  onChange={(e) => setActivityForm((f) => ({ ...f, follow_up_date: e.target.value }))}
                  data-testid="activity-form-followup-date" className={`${inputCls} bg-white`} />
                <input type="time" value={activityForm.follow_up_time} title="Time (optional)"
                  onChange={(e) => setActivityForm((f) => ({ ...f, follow_up_time: e.target.value }))}
                  data-testid="activity-form-followup-time" className={`${inputCls} bg-white`} />
              </div>
            </div>
            <button type="submit" disabled={saving} data-testid="activity-form-save"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold">
              {saving ? "Saving…" : "Log it"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
